import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import loginBackground from '../../assets/login-backgroud.webp';
import rentalCrowLogo from '../../assets/RentalCrow-Logo-Main.png';

const pageStyle = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2,
  background: `linear-gradient(rgba(25,118,210,0.65), rgba(101,51,217,0.65)), url(${loginBackground})`,
  backgroundSize: 'cover', backgroundPosition: 'center',
};

const ResetCard = ({ children }) => (
  <Paper elevation={10} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 440, borderRadius: 3, bgcolor: 'rgba(255,255,255,.92)' }}>
    <Box component="img" src={rentalCrowLogo} alt="Rental Crow" sx={{ display: 'block', width: '100%', maxWidth: 260, height: 'auto', mx: 'auto', mb: 2, borderRadius: 1 }} />
    {children}
  </Paper>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true); setError(''); setMessage('');
    try {
      const response = await axios.post(`${baseURL}/request_password_reset`, { email: email.trim().toLowerCase() });
      setMessage(response.data?.message || 'If an account exists for that email, a reset link has been sent.');
    } catch {
      setError('Unable to send the reset email right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={pageStyle}>
      <ResetCard>
        <Typography variant="h5" align="center" fontWeight={800}>Forgot password</Typography>
        <Typography align="center" color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>Enter your account email and we’ll send you a secure reset link.</Typography>
        {message ? <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert> : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField label="Email" name="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} fullWidth required autoFocus />
          <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ mt: 2, py: 1.1 }}>{loading ? 'Sending...' : 'Send reset link'}</Button>
        </Box>
        <Button component={RouterLink} to="/" fullWidth sx={{ mt: 1 }}>Back to login</Button>
      </ResetCard>
    </Box>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [validating, setValidating] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.post(`${baseURL}/validate_password_reset_token`, { token })
      .then((response) => setValidToken(Boolean(response.data?.valid)))
      .catch(() => setValidToken(false))
      .finally(() => setValidating(false));
  }, [baseURL, token]);

  const handleSubmit = async (event) => {
    event.preventDefault(); setError('');
    if (form.password.length < 8) return setError('Password must be at least 8 characters.');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match.');
    setLoading(true);
    try {
      await axios.post(`${baseURL}/reset_password`, { token, password: form.password, confirm_password: form.confirmPassword });
      navigate('/', { replace: true, state: { passwordReset: true } });
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to reset your password. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={pageStyle}>
      <ResetCard>
        <Typography variant="h5" align="center" fontWeight={800}>Create new password</Typography>
        {validating ? <Typography align="center" sx={{ my: 3 }}>Checking reset link...</Typography> : null}
        {!validating && !validToken ? <><Alert severity="error" sx={{ my: 2 }}>This reset link is invalid or has expired.</Alert><Button component={RouterLink} to="/forgot-password" variant="contained" fullWidth>Request a new link</Button></> : null}
        {!validating && validToken ? (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            <TextField label="New password" type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} fullWidth required autoFocus helperText="Use at least 8 characters." />
            <TextField label="Confirm password" type="password" value={form.confirmPassword} onChange={(event) => setForm((current) => ({ ...current, confirmPassword: event.target.value }))} fullWidth required sx={{ mt: 2 }} />
            <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ mt: 2, py: 1.1 }}>{loading ? 'Resetting...' : 'Reset password'}</Button>
          </Box>
        ) : null}
        <Button component={RouterLink} to="/" fullWidth sx={{ mt: 1 }}>Back to login</Button>
      </ResetCard>
    </Box>
  );
}
